import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";

const LINKS = [
  { label: "Why Mortlens", href: "#why" },
  { label: "Texas", href: "#texas" },
  { label: "How it works", href: "#how" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const hero = document.getElementById("hero");
      setPastHero(hero ? window.scrollY > hero.offsetHeight - 64 : false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNavigate(href);
  };

  return (
    <motion.header
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      data-testid="top-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-x-0 top-0 h-[2px] bg-gold transition-opacity duration-500 ${
          pastHero ? "opacity-100" : "opacity-0"
        }`}
      />
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="group flex items-center gap-2.5"
          aria-label="Mortlens home"
        >
          <span className="transition-transform duration-300 group-hover:-rotate-6">
            <LogoMark size={34} dark />
          </span>
          <Wordmark dark className="text-xl leading-none" />
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="group relative font-body text-sm font-medium text-white/70 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#waitlist")}
            className="min-h-[44px] rounded-lg bg-lens px-5 py-2.5 font-body text-sm font-semibold text-white transition-all duration-300 hover:bg-lens-bright hover:shadow-[0_0_24px_rgba(47,169,138,0.35)] active:scale-[0.97]"
          >
            Get my real payment
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-white md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="nav-mobile-menu"
            className="overflow-hidden border-b border-white/10 bg-ink/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 pb-6 pt-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.3 }}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-display text-2xl font-bold text-white"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="nav-mobile-cta-button"
                onClick={() => go("#waitlist")}
                className="mt-3 rounded-lg bg-lens px-5 py-3.5 font-body text-base font-semibold text-white"
              >
                Get my real payment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
